import {
  SubjectData,
  TKV2_ORDER_ATTACHMENT_API,
  extractPropertyUnitRateFeatures,
  isDefined,
} from '@aloft/toolkit-shared';
import { useQueryClient } from '@tanstack/react-query';

import { useEffect, useRef, useState } from 'react';

import {
  fetchOrchestrateAutofillPropertyAPI,
  fetchUpdateSubjectPropertyData,
  getSubjectDataQuery,
  getTkv2UnitRateModelsApiQuery,
  useGetTkv2UnitRateModelsAPI,
  usePostTkv2UnitRateModelsAPI,
} from '../../hooks/api/toolkitComponents.ts';
import { useToolkitContext } from '../../hooks/api/toolkitContext.ts';
import { setPropertyData } from '../../redux/features/propertyDataSlice.ts';
import { featuresSelectors } from '../../redux/features/selectors/featuresSelectors.ts';
import { selectPropertyDataById } from '../../redux/features/selectors/propertyDataSelectors/propertyDataSelectors.ts';
import { selectUnitRates } from '../../redux/features/selectors/unitRatesSelectors/selectUnitRates.ts';
import { setUnitRates, unitRatesMutationComplete } from '../../redux/features/unitRatesSlice.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import { SubjectPropertyDataState } from '../../redux/types.ts';

/**
 * Ensures unit rate models are computed for an order once all required subject
 * feature fields are present. Fires a single POST request (which is idempotent
 * — the backend returns the cached result if both models are already valid) and
 * then invalidates the React Query cache for the GET endpoint.
 *
 * Returns `{ isPending }` so callers can show a loading indicator while the
 * computation is in-flight. The pending state is set to `true` as soon as all
 * required features are present, before the POST even fires, to avoid a brief
 * flash of the grid while the effect is scheduled.
 */
export const useUnitRateModelsSyncDriver = (orderID: string) => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const { fetcherOptions } = useToolkitContext();
  const { mutateAsync: postUnitRateModels } = usePostTkv2UnitRateModelsAPI();
  const { selectShouldShowAdjustmentRegression } = featuresSelectors;
  const shouldShowAdjustmentRegression = useAppSelector(selectShouldShowAdjustmentRegression);

  const subjectPropertyData = useAppSelector((state) =>
    selectPropertyDataById(state, 'subject'),
  ) as SubjectPropertyDataState | undefined;

  let ready = false;
  if (subjectPropertyData) {
    const features = extractPropertyUnitRateFeatures(subjectPropertyData.subjectData);

    ready = Object.values(features).every(isDefined);
  }

  const unitRates = useAppSelector(selectUnitRates);

  const { data: persistedUnitRateData, error: unitRateGetError } = useGetTkv2UnitRateModelsAPI(
    { pathParams: { orderID } },
    {
      enabled: ready && shouldShowAdjustmentRegression && !unitRates.syncState,
      retry: false,
    },
  );

  // `loadingDone` tracks whether the GET has hydrated Redux or the POST has
  // completed (success or error). We use state so that either path triggers a
  // re-render that hides the loader.
  const [loadingDone, setLoadingDone] = useState(false);
  const hasTriggered = useRef(false);

  // When the GET returns data, hydrate Redux (only if syncState is not yet set)
  // and mark the pending state as resolved.
  useEffect(() => {
    if (!persistedUnitRateData || unitRates.syncState) return;

    const parsedData =
      TKV2_ORDER_ATTACHMENT_API.UNIT_RATE_MODELS.GET.RESPONSE.parse(persistedUnitRateData);
    dispatch(setUnitRates(parsedData.body));
    setLoadingDone(true);
  }, [persistedUnitRateData]);

  const isNotFound = (unitRateGetError as { status?: number } | null)?.status === 404;

  useEffect(() => {
    if (!ready || hasTriggered.current || !shouldShowAdjustmentRegression || !isNotFound) return;
    hasTriggered.current = true;

    // Use mutateAsync with async/await so promise resolution is handled directly
    // in the effect closure, independent of TanStack Query's observer lifecycle.
    const run = async () => {
      await postUnitRateModels({ pathParams: { orderID } });

      const autofilledPropertyData = await fetchOrchestrateAutofillPropertyAPI({
        ...fetcherOptions,
        pathParams: { orderID, propertyId: 'subject' },
        // @ts-expect-error - Type mismatch because ToolkitContext['fetcherOptions'] is of type Record<string, string>
        queryParams: { sources: ['regression', 'sensitivity'], preserveExistingValues: true },
      });

      if (autofilledPropertyData) {
        await fetchUpdateSubjectPropertyData({
          ...fetcherOptions,
          pathParams: { orderID },
          body: autofilledPropertyData as Record<string, unknown>,
        });

        // Directly update Redux state so the property grid re-renders with the
        // new unit rates immediately, without relying on the useEdgeTrigger
        // re-firing after cache invalidation (which is unreliable in React 18).
        dispatch(
          setPropertyData({
            id: 'subject',
            subjectData: autofilledPropertyData as SubjectData,
          } as SubjectPropertyDataState),
        );
      }

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getTkv2UnitRateModelsApiQuery({ pathParams: { orderID } }).queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: getSubjectDataQuery({ pathParams: { orderID } }).queryKey,
        }),
      ]);
    };

    run()
      .then(() => dispatch(unitRatesMutationComplete({ status: 'success' })))
      .catch(() => {
        dispatch(unitRatesMutationComplete({ status: 'error' }));
      })
      .finally(() => setLoadingDone(true));
  }, [
    ready,
    isNotFound,
    orderID,
    postUnitRateModels,
    queryClient,
    fetcherOptions,
    dispatch,
    shouldShowAdjustmentRegression,
  ]);

  // Show a loader whenever all required features are present but neither the
  // GET hydration nor the POST has resolved yet.
  const isPending = !loadingDone && ready && shouldShowAdjustmentRegression;

  return { isPending };
};
